import * as React from 'react';

import type { SyncApi } from './api';
import type { BackgroundTaskRow } from './types';

/** How often a followed run is read: often while it runs, rarely while it waits for an approval. */
export const POLL_RUNNING_MS = 3000;
export const POLL_WAITING_MS = 15000;

/** Follows one background-task run (`GET /v2/edit/bg_tasks/{id}/`) until it settles: every 3 s while
 *  pending or running, every 15 s while it waits for an approval, not at all once it is done, failed or
 *  cancelled. `refresh()` reads it at once (after an approve / cancel). `onSettled(task)` is called once
 *  when a followed run reaches 2, 3 or 4. */
export function useTask(api: SyncApi, taskId: string | null | undefined, onSettled?: (task: BackgroundTaskRow) => void) {
    const [task, setTask] = React.useState<BackgroundTaskRow | null>(null);
    const [error, setError] = React.useState<unknown>(null);
    const [tick, setTick] = React.useState(0);
    const settledRef = React.useRef<string | null>(null);
    const onSettledRef = React.useRef(onSettled);
    onSettledRef.current = onSettled;

    React.useEffect(() => {
        setTask(null);
        setError(null);
        settledRef.current = null;
    }, [taskId]);

    React.useEffect(() => {
        if (!taskId) {
            return undefined;
        }
        const controller = new AbortController();
        let timer: ReturnType<typeof setTimeout> | null = null;
        let stopped = false;
        const read = async () => {
            try {
                const row = await api.task(taskId, controller.signal);
                if (stopped) {
                    return;
                }
                setTask(row);
                setError(null);
                if (row.state === 0 || row.state === 1) {
                    timer = setTimeout(read, POLL_RUNNING_MS);
                } else if (row.state === 5) {
                    timer = setTimeout(read, POLL_WAITING_MS);
                } else if (settledRef.current !== taskId) {
                    settledRef.current = taskId;
                    onSettledRef.current?.(row);
                }
            } catch (e: any) {
                if (stopped || (e && e.name === 'AbortError')) {
                    return;
                }
                setError(e);
                timer = setTimeout(read, POLL_WAITING_MS);
            }
        };
        read();
        return () => {
            stopped = true;
            controller.abort();
            if (timer) {
                clearTimeout(timer);
            }
        };
    }, [api, taskId, tick]);

    const refresh = React.useCallback(() => setTick((n) => n + 1), []);
    return { task, error, refresh };
}
